import { Handler, useDrag } from "@use-gesture/react";
import classNames from "classnames";
import EventEmitter from "eventemitter3";
import { action, computed, runInAction, untracked } from "mobx";
import { observer } from "mobx-react-lite";
import { nanoid } from "nanoid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { atom } from "../atom";
import {
  DEFAULT_TEMPLATE_PANE_HEIGHT,
  DEFAULT_TEMPLATE_PANE_WIDTH,
} from "../Constants";
import { uiStateMobx } from "../SharedIframeState";
import {
  ActionType,
  EvalPaneTemplate,
  PaneType,
  Position,
  Rect,
} from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import { generatePaneId, throttle, useSyncRef } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  canvasTransformComputed,
  canvasViewport,
  focusedPaneIdAtom,
  selectedPaneIdsAtom,
} from "./CanvasState";
import { EvalPaneTemplates } from "./EvalPaneTemplates";
import { GeneratedCode } from "./GeneratedCode";
import { KeyboardCommandPriority, registerKeyboardCommand } from "./Keyboard";
import { ThemedForm } from "./PaneEvaluateTemplateComponent";
import { PaneResizers } from "./PaneResizers";
import {
  convertViewportPositionToCanvasOffset,
  OVERLAY_PANE_CLASSNAME,
  OVERLAY_PANE_HEADER_BUTTON_CLASSNAME,
  OVERLAY_PANE_HEADER_CLASSNAME,
  roundPositionToGrid,
} from "./SandboxUtils";

const shouldShowTemplateBrowserAtom = atom(false);
export function showTemplateBrowser(source: string) {
  runInAction(() => {
    shouldShowTemplateBrowserAtom.value = true;
  });
}

const dragShield = document.createElement("div");
dragShield.className =
  "fixed top-0 left-0 right-0 bottom-0 z-99999 cursor-pointer";
const TemplateSelectEventEmitter = new EventEmitter();
export function DragEvalPaneTemplatePreview() {
  const [template, setTemplate] = useState<EvalPaneTemplate | undefined>(
    undefined
  );
  const [position, setPosition] = useState<Position | undefined>();
  const positionRef = useSyncRef(position);

  const cleanupRef = useRef<(() => void) | undefined>();
  useEffect(
    () => () => {
      if (cleanupRef.current !== undefined) {
        cleanupRef.current();
      }
    },
    []
  );
  useEffect(() => {
    function onSelect({
      template,
      clientPosition,
      metadata,
    }: {
      template: EvalPaneTemplate;
      clientPosition: Position;
      metadata: any;
    }) {
      runInAction(() => {
        function updatePosition([clientX, clientY]: Position) {
          const newPosition = roundPositionToGrid(
            convertViewportPositionToCanvasOffset(
              [clientX, clientY],
              canvasTransformComputed.get(),
              canvasViewport.zoom
            ),
            1
          );
          setPosition((position) => {
            if (
              position !== undefined &&
              position[0] === newPosition[0] &&
              position[1] === newPosition[1]
            ) {
              return position;
            }
            return newPosition;
          });
        }
        function onMouseMove(e: MouseEvent) {
          updatePosition([e.clientX, e.clientY]);
        }
        const unregister = registerKeyboardCommand({
          name: "cancel template pane",
          keys: ["Escape"],
          priority: KeyboardCommandPriority.High,
          handler() {
            cleanup();
          },
        });
        function cleanup() {
          document.body.removeChild(dragShield);
          dragShield.style.cursor = "";
          window.removeEventListener("mousemove", onMouseMove);
          window.removeEventListener("click", onClick);
          unregister();
          cleanupRef.current = undefined;
          setTemplate(undefined);
          setPosition(undefined);
        }
        function onClick() {
          const position = positionRef.current!;
          const paneId = generatePaneId();
          canvasDispatch([
            ActionType.CreatePane,
            {
              paneId,
              type: PaneType.EvaluateTemplate,
              position,
              templateId: template.id,
              metadata,
            },
          ]);
          cleanup();
        }
        cleanupRef.current = cleanup;
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("click", onClick);

        setTemplate(template);
        updatePosition(clientPosition);
        document.body.appendChild(dragShield);
        focusedPaneIdAtom.value = undefined;
        selectedPaneIdsAtom.value = [];
      });
    }
    TemplateSelectEventEmitter.addListener("select", onSelect);
    return () => {
      TemplateSelectEventEmitter.removeListener("select", onSelect);
    };
  });

  if (template === undefined || position === undefined) {
    return null;
  }

  const canvasTransform = canvasTransformComputed.get();
  return (
    <div className="fixed top-0 bottom-0 left-0 right-0 z-99999">
      <div
        className="absolute"
        style={{
          transform: `translate(${canvasTransform[0]}px, ${canvasTransform[1]}px) scale(${canvasViewport.zoom})`,
        }}
      >
        <div
          className="absolute p-2 flex items-end text-neutral-400 bg-neutral-0 bg-opacity-75 border border-primary-400 rounded z-99999"
          style={{
            left: `${position[0]}px`,
            top: `${position[1]}px`,
            width: `${DEFAULT_TEMPLATE_PANE_WIDTH + 1}px`,
            height: `${DEFAULT_TEMPLATE_PANE_HEIGHT + 1}px`,
          }}
        >
          <div>
            <div>
              <span className="font-medium">click</span> to place
            </div>
            <div>
              <span className="font-medium">esc</span> to cancel
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateItem({
  template,
  isSelected,
  onClick,
}: {
  template: EvalPaneTemplate;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={classNames(
        "block w-full pr-2 py-2 text-left transition-colors",
        isSelected
          ? "border-l-2 pl-1.5 border-accent-400 bg-primary-50"
          : "hover:bg-neutral-50 pl-2"
      )}
      onClick={onClick}
    >
      <div className={classNames("font-medium text-sm mb-1")}>
        {template.schema.title}
      </div>
      <div
        className={classNames(
          isSelected ? "text-neutral-600" : "text-neutral-500"
        )}
      >
        {template.schema.description}
      </div>
    </button>
  );
}

function PreviewTemplate({ template }: { template: EvalPaneTemplate }) {
  const templateSchema = useMemo(() => {
    const schema = { ...template.schema };
    delete schema.title;
    delete schema.description;
    return schema;
  }, [template.schema]);

  const [formDataFast, setFormDataFast] = useState({});
  const [formDataSlow, setFormDataSlow] = useState(formDataFast);
  const [setFormDataSlowThrottled] = useState(() => {
    return throttle(setFormDataSlow, 300);
  });
  useEffect(() => {
    setFormDataSlowThrottled(formDataFast);
  }, [formDataFast]);
  const constructedTemplate = useMemo(
    () => template.construct(formDataSlow),
    [formDataSlow]
  );
  const [formIdPrefix] = useState(() => nanoid(8));

  return (
    <div className="flex flex-col h-full">
      <div className="grow overflow-auto">
        {Object.keys(templateSchema.properties).length > 0 ? (
          <div className="px-4 pt-3 pb-2 border-b border-neutral-200">
            <ThemedForm
              schema={templateSchema}
              uiSchema={template.uiSchema}
              formData={formDataFast}
              idPrefix={formIdPrefix}
              onChange={(e) => {
                setFormDataFast(e.formData);
              }}
              noValidate={true}
              noHtml5Validate={true}
            />
          </div>
        ) : null}
        {constructedTemplate.inputs.length > 0 ? (
          <div className="px-4 mb-2">
            <div className="mt-2 mb-1 text-sm font-medium">pane inputs</div>
            <div className="font-mono-natto text-neutral-500">
              {constructedTemplate.inputs.map((paneInput) => (
                <div
                  className="group hover:text-neutral-700 transition-colors"
                  key={paneInput.id}
                >
                  {paneInput.name}
                  {paneInput.description !== undefined ? (
                    <span className="inline-block ml-2 text-neutral-400 group-hover:text-neutral-500 transition">
                      {paneInput.description}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {constructedTemplate.panes[0].type === PaneType.Evaluate ? (
          <>
            <div className="mx-4 mt-2 mb-1 text-sm font-medium">
              generated code
            </div>
            <div className="bg-neutral-100 px-2">
              <GeneratedCode
                expression={constructedTemplate.panes[0].expression}
              />
            </div>
          </>
        ) : null}
      </div>
      <div className="border-t border-neutral-200 shrink-0 flex justify-end p-2">
        <button
          onClick={action((e) => {
            e.preventDefault();
            shouldShowTemplateBrowserAtom.value = false;
            // fire outside this onClick
            requestAnimationFrame(() => {
              TemplateSelectEventEmitter.emit("select", {
                template,
                clientPosition: [e.clientX, e.clientY],
                metadata: formDataFast,
              });
            });
          })}
          className="button-dark"
        >
          use template
        </button>
      </div>
    </div>
  );
}

const TemplateBrowserPaneImpl = observer(() => {
  const hasMaximizedPane = computed(
    () => uiStateMobx.maximizedPaneId !== undefined
  ).get();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [rectAtom] = useState(() => atom<Rect>([0, 0, 512, 384]));
  const rectComputed = useMemo(
    () => computed(() => rectAtom.value),
    [rectAtom]
  );
  const bindDrag = useDrag(
    action<Handler<"drag">>(({ target, first, last, offset }) => {
      rectAtom.value = [
        offset[0],
        offset[1],
        rectAtom.value[2],
        rectAtom.value[3],
      ];
      if (first && target instanceof HTMLElement) {
        target.style.cursor = "grabbing";
      }
      if (last && target instanceof HTMLElement) {
        target.style.cursor = "";
      }
    }),
    { from: () => untracked(() => [rectAtom.value[0], rectAtom.value[1]]) }
  );

  const [didMount, setDidMount] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  useEffect(() => {
    const mountTimeout = setTimeout(() => {
      setDidMount(true);
    }, 0);
    const animateTimeout = setTimeout(() => {
      setShouldAnimate(false);
    }, 300);
    return () => {
      clearTimeout(mountTimeout);
      clearTimeout(animateTimeout);
    };
  }, []);

  const [previewTemplate, setPreviewTemplate] = useState<
    EvalPaneTemplate | undefined
  >(undefined);

  const rect = rectAtom.value;
  return (
    <div
      className={classNames(
        OVERLAY_PANE_CLASSNAME,
        "fixed top-16 left-16 transform",
        hasMaximizedPane ? "-z-1" : "z-1",
        !didMount ? "-translate-x-8 opacity-0" : "translate-x-0 opacity-100",
        {
          transition: shouldAnimate,
        }
      )}
      style={{
        transform: `translate(${rect[0]}px, ${rect[1]}px)`,
        width: `${rect[2]}px`,
        height: `${rect[3]}px`,
      }}
      ref={rootRef}
    >
      <div className="bg-neutral-0 h-full overflow-hidden rounded shadow-xl flex flex-col dark:border dark:border-neutral-200">
        <div
          className={classNames(
            OVERLAY_PANE_HEADER_CLASSNAME,
            "h-8 px-2 flex items-center cursor-grab group shrink-0 touch-none"
          )}
          {...bindDrag()}
        >
          <button
            onClick={action(() => {
              shouldShowTemplateBrowserAtom.value = false;
            })}
            className={classNames(OVERLAY_PANE_HEADER_BUTTON_CLASSNAME, "mr-2")}
          >
            <FeatherIcon icon="x" size="xs" />
          </button>
          <div className="font-medium">pane templates</div>
          <div className="grow" />
          <Tooltip
            content={
              <div className="max-w-[16rem] text-left py-1 whitespace-normal">
                <div className="mb-2">
                  Templates allow you to create eval panes without writing code!
                </div>
                <div>Eject at any time to edit the code.</div>
              </div>
            }
            side="right"
            sideOffset={12}
          >
            <button className="flex text-neutral-300 hover:text-neutral-150 dark:text-neutral-300 dark:hover:text-neutral-400 transition-colors">
              <FeatherIcon icon="help-circle" size="md" />
            </button>
          </Tooltip>
        </div>
        <div className="flex grow overflow-hidden">
          <div className="w-1/2 overflow-auto grow border-r border-neutral-200">
            {Object.values(EvalPaneTemplates).map((template, i) => (
              <div className="border-b border-neutral-100" key={template.id}>
                <TemplateItem
                  template={template}
                  isSelected={previewTemplate === template}
                  onClick={() => {
                    setPreviewTemplate(template);
                  }}
                />
              </div>
            ))}
            <div className="p-2 text-neutral-400">
              more to come..{" "}
              <a
                href="https://twitter.com/intent/tweet?text=@_paulshen"
                target="_blank"
                rel="noreferrer"
                className="hover:text-primary-500 transition-colors"
              >
                send feedback
              </a>
            </div>
          </div>
          <div className="w-1/2">
            {previewTemplate !== undefined ? (
              <PreviewTemplate
                template={previewTemplate}
                key={previewTemplate.id}
              />
            ) : (
              <div className="p-2 text-neutral-400">select a template</div>
            )}
          </div>
        </div>
      </div>
      <PaneResizers
        shouldRoundToGrid={false}
        rectComputed={rectComputed}
        constrainSize={(size) => {
          return [Math.max(size[0], 256), Math.max(size[1], 256)];
        }}
        onUpdate={action((rect) => {
          rectAtom.value = rect;
        })}
      />
    </div>
  );
});

export const TemplateBrowserPane = observer(() => {
  if (shouldShowTemplateBrowserAtom.value) {
    return <TemplateBrowserPaneImpl />;
  }
  return null;
});
